// CommentComposerHelp — a small "Formatting & commands" link beside a comment composer that opens a
// SELECTABLE menu of the inline slash-commands (/flip, /roll, …) and the supported markdown: tapping an entry
// inserts it into the composer (via onPick) so a guest doesn't have to remember the syntax. Shared by the
// guest book (events-web) and the host/admin drawer (apps/app). The menu scrolls and never exceeds the
// viewport, so it stays fully usable on small screens. Can be driven by the composer (controlled
// `opened`/`onOpenedChange`) so typing `/help` opens it, or used stand-alone.
//
// ⚠ A ResponsiveMenu: on a phone the help is a bottom sheet of big rows (owner, 2026-10-09 — menus are hard to tap on
//   a phone), on a desktop the same dropdown. Controlled, so `/help` in the composer opens it.
import { useState } from 'react';
import { Anchor } from '@mantine/core';
import { ResponsiveMenu, type ActionSheetItem } from '@em/ui/ActionSheet';

import { COMMENT_COMMANDS, COMMENT_FORMATTING, type CommentCommandHelp } from './commentCommands';

export interface CommentComposerHelpProps {
  /** Controlled open state (so a `/help` in the composer can open it). Omit for self-managed. */
  opened?: boolean;
  onOpenedChange?: (v: boolean) => void;
  /** Insert the tapped command/snippet into the composer. */
  onPick?: (text: string) => void;
  label?: string;
}

export function CommentComposerHelp({ opened, onOpenedChange, onPick, label = 'Formatting & commands' }: CommentComposerHelpProps) {
  const [internal, setInternal] = useState(false);
  const controlled = opened !== undefined;
  const isOpen = controlled ? !!opened : internal;
  const setOpen = (v: boolean): void => {
    if (controlled) {
      onOpenedChange?.(v);
    } else {
      setInternal(v);
    }
  };

  const items = (rows: CommentCommandHelp[]): ActionSheetItem[] =>
    rows.map((r) => ({ label: r.example, description: r.desc, mono: true, onClick: () => onPick?.(r.insert ?? r.example) }));

  return (
    <ResponsiveMenu
      title={label}
      opened={isOpen}
      onOpenedChange={setOpen}
      width={340}
      position="top-start"
      // Never taller than the window (a short desktop window ran it off-screen at a fixed 380px).
      maxHeight="min(60vh, 380px)"
      // Typing goes on in the composer while it is open (a `/help` typed there opens it).
      menuProps={{ trapFocus: false, closeOnItemClick: true, styles: { dropdown: { maxWidth: 'calc(100vw - 24px)' } } }}
      target={
        <Anchor component="button" type="button" size="md" c="dimmed" onClick={() => setOpen(!isOpen)}>
          {label}
        </Anchor>
      }
      sections={[
        { label: 'Tap to insert · Commands', items: items(COMMENT_COMMANDS) },
        { label: 'Formatting', items: items(COMMENT_FORMATTING) },
      ]}
    />
  );
}
